// Theme Configuration for Survey Builder
// All themes are WCAG 2.1 AA compliant with 4.5:1+ contrast ratios

export interface Theme {
  name: string;
  displayName: string;
  colors: {
    bgPrimary: string;
    bgSecondary: string;
    bgGradientStart: string;
    bgGradientEnd: string;
    textPrimary: string;
    textSecondary: string;
    accent: string;
    accentHover: string;
    accentBg: string;
    buttonBg: string;
    buttonText: string;
    buttonHover: string;
    inputBg: string;
    inputBorder: string;
    inputFocus: string;
    cardBg: string;
    cardBorder: string;
    success: string;
    error: string;
    warning: string;
    link: string;
    modalOverlay: string;
    shadowSm: string;
    shadowMd: string;
    shadowLg: string;
  };
}

export const themes: Record<string, Theme> = {
  midnight: {
    name: 'midnight',
    displayName: 'Midnight Gold (Default)',
    colors: {
      bgPrimary: '#1a1a1a',
      bgSecondary: '#2a2a2a',
      bgGradientStart: '#1a1a1a',
      bgGradientEnd: '#000000',
      textPrimary: '#ffffff',
      textSecondary: '#ffffff',
      accent: '#FFD700',
      accentHover: '#FFA500',
      accentBg: 'rgba(255, 215, 0, 0.1)',
      buttonBg: '#FFD700',
      buttonText: '#000000',
      buttonHover: '#FFA500',
      inputBg: '#2a2a2a',
      inputBorder: '#444444',
      inputFocus: '#FFD700',
      cardBg: '#2a2a2a',
      cardBorder: '#444444',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      link: '#60a5fa',
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      shadowSm: 'rgba(0, 0, 0, 0.1)',
      shadowMd: 'rgba(0, 0, 0, 0.15)',
      shadowLg: 'rgba(0, 0, 0, 0.8)'
    }
  },
  professional: {
    name: 'professional',
    displayName: 'Professional Light',
    colors: {
      bgPrimary: '#ffffff',
      bgSecondary: '#f8fafc',
      bgGradientStart: '#ffffff',
      bgGradientEnd: '#f1f5f9',
      textPrimary: '#000000',
      textSecondary: '#000000',
      accent: '#0066cc',
      accentHover: '#0052a3',
      accentBg: 'rgba(0, 102, 204, 0.1)',
      buttonBg: '#0066cc',
      buttonText: '#ffffff',
      buttonHover: '#0052a3',
      inputBg: '#ffffff',
      inputBorder: '#cbd5e1',
      inputFocus: '#0066cc',
      cardBg: '#f8fafc',
      cardBorder: '#e2e8f0',
      success: '#16a34a',
      error: '#dc2626',
      warning: '#ea580c',
      link: '#0066cc',
      modalOverlay: 'rgba(0, 0, 0, 0.3)',
      shadowSm: 'rgba(0, 0, 0, 0.08)',
      shadowMd: 'rgba(0, 0, 0, 0.12)',
      shadowLg: 'rgba(0, 0, 0, 0.4)'
    }
  },
  navy: {
    name: 'navy',
    displayName: 'Navy Professional',
    colors: {
      bgPrimary: '#0f1c2e',
      bgSecondary: '#1a2942',
      bgGradientStart: '#0f1c2e',
      bgGradientEnd: '#08111d',
      textPrimary: '#ffffff',
      textSecondary: '#ffffff',
      accent: '#4a90e2',
      accentHover: '#357abd',
      accentBg: 'rgba(74, 144, 226, 0.1)',
      buttonBg: '#4a90e2',
      buttonText: '#ffffff',
      buttonHover: '#357abd',
      inputBg: '#1a2942',
      inputBorder: '#2d3f5f',
      inputFocus: '#4a90e2',
      cardBg: '#1a2942',
      cardBorder: '#2d3f5f',
      success: '#22c55e',
      error: '#ef4444',
      warning: '#f59e0b',
      link: '#60a5fa',
      modalOverlay: 'rgba(0, 0, 0, 0.5)',
      shadowSm: 'rgba(0, 0, 0, 0.1)',
      shadowMd: 'rgba(0, 0, 0, 0.15)',
      shadowLg: 'rgba(0, 0, 0, 0.8)'
    }
  },
  cream: {
    name: 'cream',
    displayName: 'Soft Cream',
    colors: {
      bgPrimary: '#faf8f3',
      bgSecondary: '#f5f2ea',
      bgGradientStart: '#faf8f3',
      bgGradientEnd: '#f0ebe0',
      textPrimary: '#2c2416',
      textSecondary: '#2c2416',
      accent: '#8b6914',
      accentHover: '#6b5010',
      accentBg: 'rgba(139, 105, 20, 0.1)',
      buttonBg: '#8b6914',
      buttonText: '#ffffff',
      buttonHover: '#6b5010',
      inputBg: '#ffffff',
      inputBorder: '#d4c5a9',
      inputFocus: '#8b6914',
      cardBg: '#f5f2ea',
      cardBorder: '#e6dcc8',
      success: '#2d7a3e',
      error: '#c72e2e',
      warning: '#d97706',
      link: '#1d5f8a',
      modalOverlay: 'rgba(44, 36, 22, 0.3)',
      shadowSm: 'rgba(44, 36, 22, 0.08)',
      shadowMd: 'rgba(44, 36, 22, 0.12)',
      shadowLg: 'rgba(44, 36, 22, 0.4)'
    }
  }
};

export function applyTheme(themeName: string): void {
  const theme = themes[themeName] || themes.midnight;
  const root = document.documentElement;

  // Explicitly set all CSS variables to ensure proper theme switching
  root.style.setProperty('--bg-primary', theme.colors.bgPrimary);
  root.style.setProperty('--bg-secondary', theme.colors.bgSecondary);
  root.style.setProperty('--bg-gradient-start', theme.colors.bgGradientStart);
  root.style.setProperty('--bg-gradient-end', theme.colors.bgGradientEnd);
  
  root.style.setProperty('--text-primary', theme.colors.textPrimary);
  root.style.setProperty('--text-secondary', theme.colors.textSecondary);
  
  root.style.setProperty('--accent', theme.colors.accent);
  root.style.setProperty('--accent-hover', theme.colors.accentHover);
  root.style.setProperty('--accent-bg', theme.colors.accentBg);
  
  root.style.setProperty('--button-bg', theme.colors.buttonBg);
  root.style.setProperty('--button-text', theme.colors.buttonText);
  root.style.setProperty('--button-hover', theme.colors.buttonHover);
  
  root.style.setProperty('--input-bg', theme.colors.inputBg);
  root.style.setProperty('--input-border', theme.colors.inputBorder);
  root.style.setProperty('--input-focus', theme.colors.inputFocus);
  
  root.style.setProperty('--card-bg', theme.colors.cardBg);
  root.style.setProperty('--card-border', theme.colors.cardBorder);
  
  root.style.setProperty('--success', theme.colors.success);
  root.style.setProperty('--error', theme.colors.error);
  root.style.setProperty('--warning', theme.colors.warning);
  root.style.setProperty('--link', theme.colors.link);
  
  root.style.setProperty('--modal-overlay', theme.colors.modalOverlay);
  root.style.setProperty('--shadow-sm', theme.colors.shadowSm);
  root.style.setProperty('--shadow-md', theme.colors.shadowMd);
  root.style.setProperty('--shadow-lg', theme.colors.shadowLg);

  localStorage.setItem('admin-theme', themeName);
  localStorage.setItem('admin-theme-version', '2.1'); // Version bump to force refresh
}

export function getSelectedTheme(): string {
  const version = localStorage.getItem('admin-theme-version');
  if (version !== '2.1') {
    // Clear only theme-related keys — do NOT clear user data (e.g. address book)
    localStorage.removeItem('admin-theme');
    localStorage.removeItem('admin-theme-version');
    localStorage.setItem('admin-theme-version', '2.1');
    localStorage.setItem('admin-theme', 'midnight');
    return 'midnight';
  }
  return localStorage.getItem('admin-theme') || 'midnight';
}
